import { customInstance } from "@repo/api-client";

/**
 * Client contract for the guest-directory + blacklist REST surface (`/api/v1/venues/{venueId}/...`,
 * §C.8). Goes through `@repo/api-client`'s fetch mutator (bearer token + typed `ApiError`), NOT a raw
 * fetch — a 409 stale write (tags/id-check CAS), a 403 ENTITLEMENT_REQUIRED (CRM_NOTES_TAGS off) and a
 * 400 validation come back as `ApiError` carrying the RFC 9457 problem. The Orval client is
 * regenerated from the backend OpenAPI at serial integration (`just gen`); these thin accessors are
 * the stand-in, matching the `scheduleApi`/`staffApi` pattern in this repo.
 */

export interface VisitCounters {
  completed: number;
  noShows: number;
  guestCancellations: number;
}

export interface GuestListItem {
  guestId: string;
  givenName: string | null;
  familyName: string | null;
  phone: string | null;
  email: string | null;
  tags: string[];
  completed: number;
  noShows: number;
  guestCancellations: number;
  idCheckRequired: boolean;
  blacklisted: boolean;
  version: number;
}

export interface GuestPage {
  items: GuestListItem[];
  nextCursor: string | null;
}

export interface GuestNote {
  id: string;
  body: string;
  kind: "NOTE" | "QUICK_FLAG";
  createdAt: string;
}

export interface GuestVisit {
  bookingId: string;
  serviceDay: string;
  startSec: number;
  partySize: number;
  status: string;
}

export interface GuestProfile {
  guestId: string;
  givenName: string | null;
  familyName: string | null;
  phone: string | null;
  email: string | null;
  language: string;
  tags: string[];
  completed: number;
  noShows: number;
  guestCancellations: number;
  idCheckRequired: boolean;
  anonymizedAt: string | null;
  notes: GuestNote[];
  visits: GuestVisit[];
  version: number;
}

export interface MergeConflict {
  id: string;
  phoneGuest: GuestSummary;
  emailGuest: GuestSummary;
  bookingId: string | null;
  detectedAt: string;
}

export interface GuestSummary {
  guestId: string;
  givenName: string | null;
  familyName: string | null;
  phone: string | null;
  email: string | null;
}

export type BlacklistMode = "BLOCK" | "FLAG";
export type BlacklistMatcherKind = "PHONE" | "EMAIL" | "NAME_ONLY";

export interface BlacklistEntry {
  entryId: string;
  matcherKind: BlacklistMatcherKind;
  matcherLabel: string;
  mode: BlacklistMode;
  reason: string;
  state: "ACTIVE" | "LAPSED" | "REMOVED";
  expiresAt: string;
  createdAt: string;
  renewals: Array<{ reason: string; renewedAt: string; newExpiresAt: string }>;
  appeals: Array<{
    id: string;
    state: string;
    note: string | null;
    decidedAt: string | null;
    createdAt: string;
  }>;
  version: number;
}

export interface CreateBlacklistRequest {
  matcherType: BlacklistMatcherKind;
  phone?: string;
  email?: string;
  name?: string;
  mode?: BlacklistMode;
  reason: string;
  escalateNameToBlock?: boolean;
}

const base = (venueId: string) => `/api/v1/venues/${encodeURIComponent(venueId)}/guests`;
const blBase = (venueId: string) => `/api/v1/venues/${encodeURIComponent(venueId)}/blacklist`;

export const guestsApi = {
  list: (venueId: string, search?: string, cursor?: string) => {
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (cursor) q.set("cursor", cursor);
    const qs = q.toString();
    return customInstance<GuestPage>(`${base(venueId)}${qs ? `?${qs}` : ""}`, { method: "GET" });
  },

  profile: (venueId: string, guestId: string) =>
    customInstance<GuestProfile>(`${base(venueId)}/${encodeURIComponent(guestId)}`, {
      method: "GET",
    }),

  updateTags: (venueId: string, guestId: string, version: number, tags: string[]) =>
    customInstance<GuestProfile>(`${base(venueId)}/${encodeURIComponent(guestId)}/tags`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ version, tags }),
    }),

  setIdCheck: (venueId: string, guestId: string, version: number, idCheckRequired: boolean) =>
    customInstance<GuestProfile>(`${base(venueId)}/${encodeURIComponent(guestId)}/id-check`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ version, idCheckRequired }),
    }),

  addNote: (venueId: string, guestId: string, body: string) =>
    customInstance<GuestProfile>(`${base(venueId)}/${encodeURIComponent(guestId)}/notes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ body }),
    }),

  quickFlag: (venueId: string, guestId: string, reason: string) =>
    customInstance<GuestProfile>(`${base(venueId)}/${encodeURIComponent(guestId)}/quick-flag`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    }),

  mergeConflicts: (venueId: string) =>
    customInstance<MergeConflict[]>(`${base(venueId)}/merge-conflicts`, { method: "GET" }),

  resolveConflict: (
    venueId: string,
    conflictId: string,
    resolution: "MERGE" | "DISMISS",
    winnerId?: string,
  ) =>
    customInstance<unknown>(
      `${base(venueId)}/merge-conflicts/${encodeURIComponent(conflictId)}/resolve`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resolution, winnerId }),
      },
    ),

  listBlacklist: (venueId: string, includeInactive = false) =>
    customInstance<BlacklistEntry[]>(`${blBase(venueId)}?includeInactive=${includeInactive}`, {
      method: "GET",
    }),

  reviewQueue: (venueId: string) =>
    customInstance<BlacklistEntry[]>(`${blBase(venueId)}/review-queue`, { method: "GET" }),

  createBlacklist: (venueId: string, body: CreateBlacklistRequest) =>
    customInstance<BlacklistEntry>(`${blBase(venueId)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),

  renewBlacklist: (venueId: string, entryId: string, reason: string) =>
    customInstance<BlacklistEntry>(`${blBase(venueId)}/${encodeURIComponent(entryId)}/renew`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    }),

  removeBlacklist: (venueId: string, entryId: string) =>
    customInstance<BlacklistEntry>(`${blBase(venueId)}/${encodeURIComponent(entryId)}`, {
      method: "DELETE",
    }),
};

/** The `["venue", venueId, "guests"]` query-key family this feature owns (eventQueryKeyMap convention). */
export const guestsKeys = {
  list: (venueId: string, search?: string) =>
    ["venue", venueId, "guests", "list", search ?? ""] as const,
  profile: (venueId: string, guestId: string) =>
    ["venue", venueId, "guests", "profile", guestId] as const,
  conflicts: (venueId: string) => ["venue", venueId, "guests", "conflicts"] as const,
  blacklist: (venueId: string) => ["venue", venueId, "guests", "blacklist"] as const,
  review: (venueId: string) => ["venue", venueId, "guests", "review"] as const,
};

/** Display name from the split parts — the directory + merge picker render this (GU-1 name edge case). */
export function guestDisplayName(givenName: string | null, familyName: string | null): string {
  return [givenName, familyName].filter(Boolean).join(" ").trim() || "—";
}

/**
 * Client-side guard mirroring the server (R2/GU-3): a create/renew with a blank reason is rejected
 * BEFORE the request so the manager gets an inline error, not a round-trip 400.
 */
export function validateBlacklistDraft(draft: CreateBlacklistRequest): string | null {
  if (!draft.reason.trim()) return "reasonRequired";
  if (draft.matcherType === "PHONE" && !draft.phone?.trim()) return "phoneRequired";
  if (draft.matcherType === "EMAIL" && !draft.email?.trim()) return "emailRequired";
  if (draft.matcherType === "NAME_ONLY" && !draft.name?.trim()) return "nameRequired";
  return null;
}
